import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { errorMessage } from '../../lib/api';
import { prerequisiteInfo } from '../../lib/prereq';
import { spring } from './motion';

type ToastKind = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  action?: { label: string; to?: string; onClick?: () => void };
}

interface ToastOpts {
  /** How long it stays up (default 4.5 s; errors 8 s). */
  durationMs?: number;
}

interface ToastApi {
  success: (message: string, action?: ToastItem['action'], opts?: ToastOpts) => void;
  info: (message: string, action?: ToastItem['action'], opts?: ToastOpts) => void;
  error: (err: unknown, action?: ToastItem['action']) => void;
}

const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const idRef = useRef(1);
  const remove = useCallback((id: number) => setItems((l) => l.filter((t) => t.id !== id)), []);
  const push = useCallback(
    (kind: ToastKind, message: string, action?: ToastItem['action'], opts?: ToastOpts) => {
      const id = idRef.current++;
      setItems((l) => [...l.slice(-3), { id, kind, message, action }]);
      setTimeout(() => remove(id), opts?.durationMs ?? (kind === 'error' ? 8000 : 4500));
    },
    [remove],
  );
  const api = useMemo<ToastApi>(
    () => ({
      success: (m, a, o) => push('success', m, a, o),
      info: (m, a, o) => push('info', m, a, o),
      error: (err, a) => {
        const p = prerequisiteInfo(err);
        if (p) push('error', p.message, a ?? { label: p.linkLabel, to: p.to });
        else push('error', typeof err === 'string' ? err : errorMessage(err), a);
      },
    }),
    [push],
  );
  return (
    <Ctx.Provider value={api}>
      {children}
      <div aria-live="polite" aria-relevant="additions" className="pointer-events-none fixed inset-x-0 bottom-20 z-[100] flex flex-col items-center gap-2 px-4 md:bottom-6 md:items-end md:pr-6">
        <AnimatePresence initial={false}>
          {items.map((t) => (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={spring}
              role={t.kind === 'error' ? 'alert' : 'status'}
              className={clsx('pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-2xl border bg-surface p-3.5 shadow-pop', t.kind === 'error' ? 'border-danger/40' : 'border-line')}
            >
              {t.kind === 'success' ? (
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
              ) : t.kind === 'error' ? (
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden />
              ) : (
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[0.8125rem] text-ink">{t.message}</p>
                {t.action &&
                  (t.action.to ? (
                    <Link to={t.action.to} onClick={() => remove(t.id)} className="mt-1 inline-block text-[0.8125rem] font-semibold text-primary-ink hover:underline">
                      {t.action.label} →
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        t.action?.onClick?.();
                        remove(t.id);
                      }}
                      className="mt-1 text-[0.8125rem] font-semibold text-primary-ink hover:underline"
                    >
                      {t.action.label}
                    </button>
                  ))}
              </div>
              <button type="button" aria-label="Dismiss notification" onClick={() => remove(t.id)} className="rounded-lg p-1 text-muted hover:bg-raised">
                <X className="h-4 w-4" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const c = useContext(Ctx);
  if (!c) throw new Error('useToast outside ToastProvider');
  return c;
}
